/**
 * @file AI Gallery「发布为网页」弹窗
 * 
 * API 字段规范：
 * - name_cn: string(1-40) 作品中文名（必填）
 * - name_en: string(1-60) 作品英文名（必填）
 * - desc_cn: string(0-200) 中文描述
 * - desc_en: string(0-300) 英文描述
 * - source: enum (link / file)（必填）
 * - link: string(URL) source=link 时必填
 * - tags: TagRef[] 1-5 个标签，元素 {id: 1..9}（必填）
 * - visibility: enum (self / user / public)（必填）
 * - share_users: string[] visibility=user 时至少 1 个 openid
 * - file: binary 仅 multipart 模式；ZIP 或 HTML 二进制
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useUpdateEffect } from 'ahooks';
import { Modal, notification } from 'tea-component';
import { useIntl } from 'umi';
import SvgIcon from '@home/components/SvgIcon';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';
import {
  UseFormSearch,
  type ISearchRef,
} from '@gp/pages/Pipeline/ProjectDetails/components/GameOverviewEdit/Components/UseFormSearch';
import { useDownloadNotification } from '../../../../../ChatPage/components/DownloadLink/hooks';
import { normalizeAiGalleryPublishVisibilityPayload } from './constants';
import { useAiGalleryPublishFormConfig } from '../../hooks/useAiGalleryPublishFormConfig';
import {
  useAiGalleryTags,
  useAiGalleryUserSearch,
  useAiGalleryCreate,
  useAiGalleryCreateTag,
  type IAiGalleryCreateParams,
  type TAiGalleryVisibility,
} from '../../hooks/useAiGalleryPublishApi';
import './index.scss';

/**
 * 提交「发布为网页」接口的表单载荷（前端使用）
 */
export interface IAiGalleryPublishFormValues {
  /** 作品中文名 (1-40) */
  nameCn: string;
  /** 作品英文名 (1-60) */
  nameEn: string;
  /** 中文描述 (0-200) */
  descCn: string;
  /** 英文描述 (0-300) */
  descEn: string;
  /** 数据类型：real=真实数据，demo=Demo 数据 */
  dataMode: 'real' | 'demo';
  /** 标签 ID 列表 (1-5 个) */
  tags: number[];
  /** 访问权限 */
  visibility: TAiGalleryVisibility;
  /** 分享用户 openid 列表（visibility=user 时必填） */
  shareUsers: string[];
}

export interface IAiGalleryPublishModalProps {
  /** 是否展示弹窗 */
  visible: boolean;
  /** 默认中文名称（一般为当前 HTML 文件名） */
  defaultNameCn: string;
  /** 签名后的 COS URL（用于 fetch 转 File 上传） */
  cosSignedUrl?: string;
  /** 关闭弹窗 */
  onClose: () => void;
  /** 用户确认发布 */
  onConfirm: (values: IAiGalleryPublishFormValues) => void;
  /** 发布成功回调 */
  onPublishSuccess?: (result: { id: string; rule_id?: number; rule_key?: string; url?: string; }) => void;
}

/**
 * AI Gallery：点击「发布」后的「发布为网页」表单弹窗。
 * visible=false 时不渲染任何内容
 */
export const AiGalleryPublishModal: React.FC<IAiGalleryPublishModalProps> = ({
  visible,
  cosSignedUrl = '',
  onClose,
  onConfirm,
  onPublishSuccess,
}) => {
  const { formatMessage } = useIntl();
  const { sessionId } = useSearchParamsAppoint();
  const searchFormRef = useRef<ISearchRef | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setNoticeStatus } = useDownloadNotification(undefined, true, {
    loadingText: formatMessage({ id: 'dba_ai_gallery_publish_submitting' }),
    successText: formatMessage({ id: 'dba_ai_gallery_publish_success' }),
    errorText: formatMessage({ id: 'dba_ai_gallery_publish_failed' }),
  });

  // API Hooks
  const { data: tagsData, loading: tagsLoading, run: fetchTags } = useAiGalleryTags();
  const { searchUsers, loading: searchLoading } = useAiGalleryUserSearch();
  const { createGallery } = useAiGalleryCreate();
  const { createTag, loading: createTagLoading } = useAiGalleryCreateTag();

  // 弹窗打开时：请求标签列表 + 重置表单
  useUpdateEffect(() => {
    if (visible) {
      fetchTags();
      searchFormRef.current?.form?.reset?.();
    }
  }, [visible]);

  // 创建自定义标签回调，返回新标签 ID，成功后刷新列表
  const handleCreateTag = useCallback(
    async (params: { name_cn: string; name_en: string; }): Promise<number | null> => {
      const result = await createTag(params);
      if (result?.id) {
        fetchTags();
      }
      return result?.id ?? null;
    },
    [createTag, fetchTags],
  );

  // 表单配置（传入 API 相关参数）
  const { config } = useAiGalleryPublishFormConfig({
    tagOptions: tagsData?.tags,
    tagsLoading,
    onCreateTag: handleCreateTag,
    createTagLoading,
    onSearchUsers: searchUsers,
    searchLoading,
  });

  const initialValues = useMemo(
    () => ({
      nameCn: '',
      nameEn: '',
      descCn: '',
      descEn: '',
      dataMode: 'demo' as const,
      tags: [] as number[],
      visibilityPayload: {
        visibility: 'self' as const,
        shareUsers: [] as Array<{ openid: string; username: string; email?: string; avatar?: string; }>,
      },
    }),
    [],
  );

  const handleFormSubmit = useCallback(
    async (value: unknown) => {
      const raw = value as Record<string, unknown>;
      const visibilityPayload = normalizeAiGalleryPublishVisibilityPayload(raw.visibilityPayload);
      const { visibility, shareUsers } = visibilityPayload;

      // 表单校验：visibility=user 模式必须有用户
      if (visibility === 'user' && shareUsers.length === 0) {
        notification.warning({
          description: formatMessage({ id: 'dba_ai_gallery_publish_share_users_required' }),
          duration: 2600,
        });
        return;
      }

      // 表单校验：标签必填
      const tags = Array.isArray(raw.tags) ? (raw.tags as number[]) : [];
      if (tags.length === 0) {
        notification.warning({
          description: formatMessage({ id: 'dba_ai_gallery_publish_tags_required' }),
          duration: 2600,
        });
        return;
      }

      // 构建表单数据（前端使用）
      const nameCnVal = String(raw.nameCn ?? '').trim();
      const nameEnVal = String(raw.nameEn ?? '').trim();
      const dataModeVal = (raw.dataMode as 'real' | 'demo') || 'demo';
      const formValues: IAiGalleryPublishFormValues = {
        nameCn: nameCnVal,
        nameEn: nameEnVal || nameCnVal, // 英文标题为空时使用中文标题
        descCn: String(raw.descCn ?? '').trim(),
        descEn: String(raw.descEn ?? '').trim(),
        dataMode: dataModeVal,
        tags,
        visibility,
        shareUsers: visibility === 'user' ? shareUsers.map((u: { openid: string; }) => u.openid) : [],
      };

      // 调用创建作品 API
      setIsSubmitting(true);
      setNoticeStatus('loading');
      try {
        // source=file 时，从签名 COS URL fetch 下来转成 File 对象
        let htmlFile: File | undefined;
        if (cosSignedUrl) {
          const response = await fetch(cosSignedUrl);
          if (!response.ok) {
            throw new Error(`获取 HTML 文件失败: ${response.status}`);
          }
          const blob = await response.blob();
          htmlFile = new File([blob], `${formValues.nameCn || 'gallery'}.html`, {
            type: 'text/html',
          });
        }

        const params: IAiGalleryCreateParams = {
          name_cn: formValues.nameCn,
          name_en: formValues.nameEn,
          desc_cn: formValues.descCn || undefined,
          desc_en: formValues.descEn || undefined,
          source: 'file',
          file: htmlFile,
          tags: formValues.tags.map(id => ({ id })),
          visibility: formValues.visibility,
          share_users: formValues.visibility === 'user' ? formValues.shareUsers : undefined,
          data_mode: formValues.dataMode,
        };

        const result = await createGallery(params);

        if (result) {
          setNoticeStatus('success');
          buriedBtnPoint({
            pageId: '700501',
            buttonId: '700501060',
            extInfo: JSON.stringify({ sessionId, msg: 'HTML published' }),
            typeId: 'aigc',
          });
          onPublishSuccess?.(result);
          onConfirm(formValues);
        } else {
          setNoticeStatus('error');
        }
      } catch (error) {
        console.error('发布失败:', error);
        setNoticeStatus('error');
      } finally {
        setIsSubmitting(false);
      }
    },
    [formatMessage, onConfirm, onPublishSuccess, createGallery, cosSignedUrl],
  );

  const handleModalClose = (): void => {
    searchFormRef.current?.onclose?.();
    onClose();
  };

  return (
    <Modal
      visible={visible}
      className="dba-ai-gallery-publish-modal"
      caption={formatMessage({ id: 'dba_ai_gallery_publish_modal_caption' })}
      onClose={handleModalClose}
      disableCloseIcon
    >
      <div className="dba-ai-gallery-publish-modal__close-btn" onClick={handleModalClose}>
        <SvgIcon type="clonse" />
      </div>
      <Modal.Body>
        <div className="dba-ai-gallery-publish-modal__scroll">
          {UseFormSearch({
            config,
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            getFormValue: handleFormSubmit,
            labelSpan: 6,
            column: 1,
            isUnpack: false,
            initialValues,
            ref: searchFormRef,
            isShowSearchBtn: false,
            className: 'dba-ai-gallery-publish-modal__form dbl-search-container',
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="dba-ai-gallery-publish-modal__footer-buttons">
          <button
            type="button"
            className="dba-ai-gallery-publish-modal__submit-btn"
            onClick={() => searchFormRef.current?.handleSubmit?.()}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? formatMessage({ id: 'dba_ai_gallery_publish_submitting' })
              : formatMessage({ id: 'dba_ai_gallery_publish_confirm' })}
          </button>
          <button
            type="button"
            className="dba-ai-gallery-publish-modal__cancel-btn"
            onClick={handleModalClose}
            disabled={isSubmitting}
          >
            {formatMessage({ id: 'dba_cancel' })}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AiGalleryPublishModal;
